import { fromMaybe } from "../../lib/maybe";
/**
 * Entity underlines for the ProseMirror editor (#250): the matching in
 * `entityPeekIndex.ts`, run over the plain-text projection and
 * mapped back to document ranges. The hover / caret peek card is a
 * `hoverCards` source (see `peekHoverSource`).
 */

import {
  Plugin,
  PluginKey,
  type EditorState,
  type Transaction,
} from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";
import type { Node as PMNode } from "prosemirror-model";
import { matchMaybe } from "../../lib/maybe";
import {
  entityEffectiveColor,
  entityRefAccentAttributes,
} from "../../lib/entityColors";
import {
  blockIndexAt,
  textOffsetToDocPos,
  type PlainTextProjection,
} from "../../lib/zwdoc/plainText";
import {
  CARET_PEEK_MS,
  HOVER_TIME_MS,
  editorContentWidth,
  renderEntityPeekCard,
  type EntityPeekManuscriptContext,
} from "../entityPeekCard";
import {
  entityPeekMatchAt,
  findEntityPeekMatches,
  type EntityPeekEntry,
  type EntityPeekMatch,
} from "../entityPeekIndex";
import type { HoverSource } from "./hoverCards";
import { projectionOf } from "./projection";

/** A match with both its document range and its projection range. */
export type ProseEntityMatch = EntityPeekMatch & {
  textFrom: number;
  textTo: number;
};

/** Shell context the peek card needs (avatar root, arc, navigation). */
export type ProsePeekContext = {
  rootPath: () => string | null;
  manuscript: () => EntityPeekManuscriptContext | null;
  openEntry: (fileName: string) => void;
};

type PeekState = {
  matches: readonly ProseEntityMatch[];
  decorations: DecorationSet;
  /** Projection the matches were built from. The next edit diffs against it. */
  projection: PlainTextProjection;
};

/** Re-scan the whole document when an edit touches more prose than this. */
const INCREMENTAL_COVER_LIMIT = 8_000;

const peekKey = new PluginKey<PeekState>("zwEntityPeek");

function decorate(
  doc: PMNode,
  matches: readonly ProseEntityMatch[],
): DecorationSet {
  const decorations = matches.flatMap((match) => {
    if (match.from >= match.to) return [];
    const accent = entityRefAccentAttributes(
      entityEffectiveColor(match.entry.color, match.entry.type),
    );
    return [
      Decoration.inline(match.from, match.to, {
        class: "zw-entity-ref",
        "data-entity-id": match.entry.fileName,
        ...matchMaybe(accent, {
          nothing: () => ({}),
          just: (attributes) => attributes,
        }),
      }),
    ];
  });
  return DecorationSet.create(doc, decorations);
}

function matchesIn(
  projection: PlainTextProjection,
  index: readonly EntityPeekEntry[],
  textFrom: number,
  textTo: number,
): ProseEntityMatch[] {
  return findEntityPeekMatches(
    projection.text.slice(textFrom, textTo),
    index,
    false,
  ).map((match): ProseEntityMatch => {
    const from = match.from + textFrom;
    const to = match.to + textFrom;
    return {
      entry: match.entry,
      textFrom: from,
      textTo: to,
      from: textOffsetToDocPos(projection, from),
      to: textOffsetToDocPos(projection, to),
    };
  });
}

function peekFromProjection(
  doc: PMNode,
  index: readonly EntityPeekEntry[],
  projection: PlainTextProjection,
): PeekState {
  if (index.length === 0) {
    return { matches: [], decorations: DecorationSet.empty, projection };
  }
  const matches = matchesIn(projection, index, 0, projection.text.length);
  return { matches, decorations: decorate(doc, matches), projection };
}

function needleLength(index: readonly EntityPeekEntry[]): number {
  return index.reduce((max, entry) => {
    const plural = matchMaybe(entry.plural, {
      nothing: () => 0,
      just: (value) => value.length,
    });
    const adjective = matchMaybe(entry.adjective, {
      nothing: () => 0,
      just: (value) => value.length,
    });
    const aka = entry.aka.reduce(
      (longest, name) => Math.max(longest, name.length),
      0,
    );
    return Math.max(max, entry.displayName.length, aka, plural, adjective);
  }, 0);
}

/** First differing index, and the ends of the replaced slices. */
function textEditSpan(
  before: string,
  after: string,
): { from: number; oldTo: number; newTo: number } | null {
  if (before === after) return null;
  const limit = Math.min(before.length, after.length);
  let prefix = 0;
  while (
    prefix < limit &&
    before.charCodeAt(prefix) === after.charCodeAt(prefix)
  ) {
    prefix += 1;
  }
  let suffix = 0;
  const suffixLimit = limit - prefix;
  while (
    suffix < suffixLimit &&
    before.charCodeAt(before.length - 1 - suffix) ===
      after.charCodeAt(after.length - 1 - suffix)
  ) {
    suffix += 1;
  }
  return {
    from: prefix,
    oldTo: before.length - suffix,
    newTo: after.length - suffix,
  };
}

/** Block bounds covering `[from, to)` in the projection, widened to whole blocks. */
function coverBlocks(
  projection: PlainTextProjection,
  from: number,
  to: number,
): { from: number; to: number } | null {
  const { blocks, text } = projection;
  if (blocks.length === 0) return null;
  const start = Math.max(0, Math.min(from, text.length));
  const end = Math.max(start, Math.min(to, text.length));
  const lastChar = Math.max(0, text.length - 1);
  const startIndex = blockIndexAt(
    projection,
    text.length === 0 ? 0 : Math.min(start, lastChar),
  );
  const endIndex = blockIndexAt(
    projection,
    text.length === 0 ? 0 : Math.min(Math.max(end - 1, start), lastChar),
  );
  if (startIndex < 0 || endIndex < 0) return null;
  const first = blocks[startIndex];
  const last = blocks[Math.max(startIndex, endIndex)];
  if (first === undefined || last === undefined) return null;
  return { from: first.textFrom, to: last.textFrom + last.length };
}

function remapPeek(
  previous: PeekState,
  tr: Transaction,
  doc: PMNode,
  projection: PlainTextProjection,
): PeekState {
  const matches = previous.matches.flatMap((match): ProseEntityMatch[] => {
    const from = tr.mapping.map(match.from, 1);
    const to = tr.mapping.map(match.to, -1);
    if (from >= to) return [];
    return [{ ...match, from, to }];
  });
  const decorations =
    matches.length === previous.matches.length
      ? previous.decorations.map(tr.mapping, doc)
      : decorate(doc, matches);
  return { matches, decorations, projection };
}

/**
 * Re-match only the blocks an edit touched. Names do not cross the
 * projection's block separator, so matches outside that cover stay valid
 * and only their positions move. Anything that doesn't fit (a large paste)
 * falls back to a full scan, which is what every edit used to do.
 */
function incrementalPeek(
  previous: PeekState,
  tr: Transaction,
  state: EditorState,
  index: readonly EntityPeekEntry[],
  projection: PlainTextProjection,
): PeekState {
  if (projection.text === previous.projection.text) {
    return remapPeek(previous, tr, state.doc, projection);
  }
  const span = textEditSpan(previous.projection.text, projection.text);
  const needle = needleLength(index);
  const oldCover =
    span === null
      ? null
      : coverBlocks(
          previous.projection,
          span.from - needle,
          span.oldTo + needle,
        );
  const newCover =
    span === null
      ? null
      : coverBlocks(projection, span.from - needle, span.newTo + needle);
  const delta = projection.text.length - previous.projection.text.length;
  if (
    span === null ||
    oldCover === null ||
    newCover === null ||
    oldCover.from !== newCover.from ||
    newCover.to - oldCover.to !== delta ||
    newCover.to - newCover.from > INCREMENTAL_COVER_LIMIT
  ) {
    return peekFromProjection(state.doc, index, projection);
  }
  const created = matchesIn(projection, index, newCover.from, newCover.to);
  const kept = previous.matches.flatMap((match): ProseEntityMatch[] => {
    const overlaps =
      match.textTo > oldCover.from && match.textFrom < oldCover.to;
    if (overlaps) return [];
    const shift = match.textFrom >= oldCover.to ? delta : 0;
    const from = tr.mapping.map(match.from, 1);
    const to = tr.mapping.map(match.to, -1);
    if (from >= to) return [];
    return [
      {
        ...match,
        textFrom: match.textFrom + shift,
        textTo: match.textTo + shift,
        from,
        to,
      },
    ];
  });
  const matches = [...kept, ...created].sort((a, b) => {
    if (a.textFrom !== b.textFrom) return a.textFrom - b.textFrom;
    return a.textTo - b.textTo;
  });
  return { matches, decorations: decorate(state.doc, matches), projection };
}

/**
 * Underlines Database entity names from `index()`. A keystroke re-matches
 * only the blocks it changed; `refreshEntityPeek` scans the whole document.
 * Must come after `projectionPlugin`.
 */
export function entityPeekPlugin(
  index: () => readonly EntityPeekEntry[],
): Plugin<PeekState> {
  return new Plugin<PeekState>({
    key: peekKey,
    state: {
      init: (_config, state) =>
        peekFromProjection(state.doc, index(), projectionOf(state)),
      apply: (tr, value, _old, state) => {
        if (tr.docChanged === false && tr.getMeta(peekKey) !== true)
          return value;
        const entries = index();
        const projection = projectionOf(state);
        if (tr.getMeta(peekKey) === true) {
          return peekFromProjection(state.doc, entries, projection);
        }
        return incrementalPeek(value, tr, state, entries, projection);
      },
    },
    props: {
      decorations: (state) => peekKey.getState(state)?.decorations ?? null,
    },
  });
}

/** Re-read the index (it changed outside the editor). */
export function refreshEntityPeek(view: EditorView): void {
  view.dispatch(view.state.tr.setMeta(peekKey, true));
}

export function entityMatchesOf(
  state: EditorState,
): readonly ProseEntityMatch[] {
  return peekKey.getState(state)?.matches ?? [];
}

export function entityMatchAt(
  state: EditorState,
  pos: number,
  side: -1 | 1,
): ProseEntityMatch | null {
  return fromMaybe(entityPeekMatchAt(entityMatchesOf(state), pos, side));
}

/** Hover and resting-caret peek card for entity names. */
export function peekHoverSource(context: ProsePeekContext): HoverSource {
  return {
    hoverDelayMs: HOVER_TIME_MS,
    caretDelayMs: CARET_PEEK_MS,
    at(view, pos, side) {
      const match = entityMatchAt(view.state, pos, side);
      if (match === null) return null;
      return {
        from: match.from,
        to: match.to,
        above: true,
        className: "zw-prose-popover-peek",
        render: () =>
          renderEntityPeekCard(match.entry, {
            contentWidth: editorContentWidth(view.dom),
            rootPath: context.rootPath(),
            manuscript: context.manuscript(),
            onOpen: context.openEntry,
          }),
      };
    },
  };
}
